import React, { useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { EventInfo, EventInfoWithoutPlayers, isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import { EventCard } from '@/components/EventCard';
import { useEventTime } from '@/components/EventTime';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { getFinalScoreColumns } from '@/feature/bets-sbk/utils/utils';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { PICKS_USE_EVENT_STALE_TIME, useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { Event, EventDetails } from '@/feature/event-details-sbk/types';
import { useSinglePlayerProfile } from '@/feature/fantasy-event-sheets/SinglePlayerProfileProvider';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { useEventDetail } from '@/hooks/use-sbk-event-details-cache';
import { RootStackParamList } from '@/navigation/types';
import { EventAttributesType, PlayerAttributesType } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { findEventAttribute, findPlayerAttribute } from '@/utils/fantasy-attribute-utils';
import { formatPlayerName } from '@/utils/format-player-name';
import { gameUtils } from '@/utils/games';
import { isPGA, isTennis } from '@/utils/league';
import { getTennisMatchup, orderTennisMatchupByPamSides } from '@/utils/tennis-matchup';

import { EventCardPlayerIcon } from './EventCardPlayerIcon';
import { PGAEventCard } from './PGAEventCard';

type PickemEventCardProps = {
    pickemEvent: EventInfo | EventInfoWithoutPlayers;
    index: number;
    variant: 'lobby' | 'eventDetails';
    isSelected?: boolean;
};

/**
 * fetches detailed event info and conditionally renders a PAM-connected or "simple" card.
 * - PAM-connected - we have a PAM event ID, so we can fetch event score and clock and subscribe to updates
 * - simple - we only have basic event info from Pickem BE
 * this card is used in the lobby events list, and it requests all the event info from Pickem BE
 * so we can load the event details sheet smoothly
 */
export const PickemEventLobbyCard = ({ pickemEvent, index, variant }: PickemEventCardProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;

    const eventTime = gameUtils.createTimeLabel(pickemEvent);

    return pamEventId ? (
        <PickemEventCardWithPam pickemEvent={pickemEvent} index={index} pamEventId={pamEventId} variant={variant} />
    ) : (
        <PickemEventCardSimple pickemEvent={pickemEvent} index={index} eventTitle={eventTime} variant={variant} />
    );
};

export const PickemEventDetailsCard = ({ pickemEvent, index, variant, isSelected }: PickemEventCardProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;

    const eventTime = gameUtils.createTimeLabel(pickemEvent);

    return pamEventId ? (
        <PickemEventCardWithPam
            pickemEvent={pickemEvent}
            index={index}
            pamEventId={pamEventId}
            variant={variant}
            isSelected={isSelected}
        />
    ) : (
        <PickemEventCardSimple
            pickemEvent={pickemEvent}
            index={index}
            eventTitle={eventTime}
            variant={variant}
            isSelected={isSelected}
        />
    );
};

/**
 * this card is used for events that are not connected to PAM
 * most of the information displayed is using the data from the Pickem BE
 * optionally, you can send the final score and event title to display it on the card
 * this is used by PAM-connected cards to display the final score
 */

const PickemEventCardSimple = ({
    pickemEvent,
    eventTitle,
    awayScore,
    homeScore,
    variant,
    isSelected,
    index,
    pamEvent,
}: PickemEventCardProps & { eventTitle: string; awayScore?: number; homeScore?: number; pamEvent?: Event }) => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const { isPlayerProfileModalOpen, closeSinglePlayerProfile } = useSinglePlayerProfile();

    const navigateToEventDetailSheet = useCallback(() => {
        useEventsTabsStore.getState().actions.setSelectedTab(index);

        if (isPlayerProfileModalOpen?.value) {
            closeSinglePlayerProfile?.();
        }

        if (isWeb) {
            navigation.setParams({
                eventId: pickemEvent.id,
            });
        }
        if (variant === 'eventDetails') {
            BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PAGER_EVENT_CLICK);
        }
        if (variant === 'lobby') {
            navigation.navigate('FantasyEventsSheets', {
                league: pickemEvent.league,
                eventId: pickemEvent.id || '',
                contextKey: new Date().getTime().toString(),
            });
        }
    }, [
        closeSinglePlayerProfile,
        index,
        isPlayerProfileModalOpen?.value,
        navigation,
        pickemEvent.id,
        pickemEvent.league,
        variant,
    ]);

    if (isPGA(pickemEvent.league)) {
        return <PGAEventCard event={pickemEvent} onPress={navigateToEventDetailSheet} isSelected={isSelected} />;
    }

    if (isTennis(pickemEvent.league) && isIndividualEvent(pickemEvent)) {
        // order by PAM event when we have it to avoid mismatch between the player and score
        const [player1, player2] = orderTennisMatchupByPamSides(
            getTennisMatchup(pickemEvent.players, pickemEvent.name),
            pamEvent
        );
        const player1Rank = findPlayerAttribute(player1.player, PlayerAttributesType.TennisRank);
        const player2Rank = findPlayerAttribute(player2.player, PlayerAttributesType.TennisRank);

        return (
            <EventCard
                onPress={navigateToEventDetailSheet}
                variant={variant}
                awayTeamIcon={player1.icon ?? ''}
                awayTeamIconComponent={<EventCardPlayerIcon uri={player1.icon} />}
                awayTeamShortCode={formatPlayerName(player1)}
                homeTeamIcon={player2.icon ?? ''}
                homeTeamIconComponent={<EventCardPlayerIcon uri={player2.icon} />}
                homeTeamShortCode={formatPlayerName(player2)}
                isLive={gameUtils.isLive(pickemEvent)}
                eventTitle={eventTitle}
                league={pickemEvent.league}
                isSelected={isSelected}
                awayScore={awayScore}
                teamInfo={[player1Rank ? `#${player1Rank}` : null, player2Rank ? `#${player2Rank}` : null]}
                homeScore={homeScore}
                shortDescription={findEventAttribute(pickemEvent, EventAttributesType.Name)}
                index={index}
            />
        );
    }

    if (!isTeamEvent(pickemEvent)) {
        return null;
    }

    const teams = eventUtils.getTeams(pickemEvent);

    if (!teams[0] || !teams[1]) {
        return null;
    }

    return (
        <EventCard
            onPress={navigateToEventDetailSheet}
            variant={variant}
            awayTeamIcon={teams[0].icon}
            awayTeamShortCode={teams[0].name}
            homeTeamIcon={teams[1].icon}
            homeTeamShortCode={teams[1].name}
            isLive={gameUtils.isLive(pickemEvent)}
            eventTitle={eventTitle}
            league={pickemEvent.league}
            awayPrimaryTeamColor={teams[0].color}
            homePrimaryTeamColor={teams[1].color}
            awaySecondaryTeamColor={teams[0].secondaryColor}
            homeSecondaryTeamColor={teams[1].secondaryColor}
            awayScore={awayScore}
            homeScore={homeScore}
            isSelected={isSelected}
            index={index}
        />
    );
};

/**
 * this card is used for events that are connected to PAM
 * it fetches the event details and conditionally renders a PAM-connected
 * if we have errors fetching the event, we display a simple card
 * if the event is live, we subscribe to updates and display the score
 */
const PickemEventCardWithPam = ({
    pickemEvent,
    index,
    pamEventId,
    variant,
    isSelected,
}: PickemEventCardProps & { pamEventId: string }) => {
    //do not make PAM API calls if we know that the event is not LIVE from the pickem API data
    const { data: event, error } = useEvent(pamEventId, {
        enabled: gameUtils.isLive(pickemEvent),
        staleTime: PICKS_USE_EVENT_STALE_TIME,
    });

    const eventDetails = useEventDetail(event?.id ?? '') ?? event?.event_details;

    if (event && !error && eventDetails.status?.toUpperCase() === 'LIVE') {
        return (
            <PickemEventCardWithScore
                variant={variant}
                event={event}
                pickemEvent={pickemEvent}
                index={index}
                eventDetails={eventDetails}
                isSelected={isSelected}
            />
        );
    }

    const eventTitle = gameUtils.createTimeLabel(pickemEvent);

    return (
        <PickemEventCardSimple
            variant={variant}
            pickemEvent={pickemEvent}
            index={index}
            eventTitle={eventTitle}
            isSelected={isSelected}
        />
    );
};

/**
 * this card is used for events that are connected to PAM, are live
 * and we can subscribe to updates
 */
const PickemEventCardWithScore = ({
    pickemEvent,
    index,
    event,
    eventDetails,
    variant,
    isSelected,
}: PickemEventCardProps & {
    event: Event;
    eventDetails: EventDetails;
}) => {
    const finalScores = useMemo(
        () => (event && eventDetails ? getFinalScoreColumns(eventDetails, event.sport)[0] : undefined),
        [event, eventDetails]
    );

    const matchDetails = useEventTime(event);

    const eventTitle = gameUtils.createTimeLabel(pickemEvent);

    return (
        <PickemEventCardSimple
            variant={variant}
            index={index}
            pickemEvent={pickemEvent}
            pamEvent={event}
            eventTitle={matchDetails || eventTitle}
            awayScore={finalScores?.awayScore}
            homeScore={finalScores?.homeScore}
            isSelected={isSelected}
        />
    );
};
